import { ShoppingCart, Search, RefreshCw, Eye, FileText, CreditCard } from 'lucide-react'

/**
 * BillingActionBar — shared sticky top action bar for billing screens.
 *
 * Extracted so Multi-Item Billing (and, later, US / Indian Billing) present the
 * same layout: title + secondary actions on the left, primary actions in the
 * centre, live rate pills on the right. The bar is presentational only — every
 * action is delegated through a callback prop; omit a callback to hide its
 * button.
 *
 * Props:
 *   title, subtitle
 *   onLoadEstimate, onClear                       — left secondary actions
 *   onView, onInvoicePdf, onCheckout              — centre primary actions
 *   viewDisabled, invoiceDisabled, checkoutDisabled
 *   checkoutLabel                                 — defaults to 'Checkout'
 *   goldRate  {string|number|null}                — ₹ 24K/g pill (hidden when null/'')
 *   usdRate   {string|number|null}                — ₹/USD pill
 *   gstRate   {string|number|null}                — GST % pill
 *   rightExtra {ReactNode}                        — optional node after the pills
 */
export default function BillingActionBar({
  title = 'Billing',
  subtitle,
  onLoadEstimate,
  onClear,
  onView,
  onInvoicePdf,
  onCheckout,
  viewDisabled = false,
  invoiceDisabled = false,
  checkoutDisabled = false,
  checkoutLabel = 'Checkout',
  goldRate,
  usdRate,
  gstRate,
  rightExtra,
}) {
  const has = (v) => v !== null && v !== undefined && v !== ''

  return (
    <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm print:hidden">
      <div className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap max-w-6xl mx-auto">

        {/* Left — title + secondary actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <h1 className="text-base font-bold text-slate-800 whitespace-nowrap flex items-center gap-2">
            <ShoppingCart size={16} className="text-slate-500" />
            {title}
            {subtitle && <span className="text-xs font-normal text-slate-400">{subtitle}</span>}
          </h1>
          {(onLoadEstimate || onClear) && <div className="w-px h-5 bg-slate-200" />}
          {onLoadEstimate && (
            <button onClick={onLoadEstimate}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 transition whitespace-nowrap">
              <Search size={12} /> Load Estimate
            </button>
          )}
          {onClear && (
            <button onClick={onClear}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-red-600 transition">
              <RefreshCw size={12} /> Clear
            </button>
          )}
        </div>

        {/* Centre — primary actions */}
        {(onView || onInvoicePdf || onCheckout) && (
          <div className="flex items-center gap-1.5">
            {onView && (
              <button onClick={onView} disabled={viewDisabled}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40 transition">
                <Eye size={13} /> View
              </button>
            )}
            {onInvoicePdf && (
              <button onClick={onInvoicePdf} disabled={invoiceDisabled}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-700 hover:bg-slate-800 text-white disabled:opacity-40 transition whitespace-nowrap">
                <FileText size={13} /> Invoice PDF
              </button>
            )}
            {onCheckout && (
              <button onClick={onCheckout} disabled={checkoutDisabled}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 transition whitespace-nowrap">
                <CreditCard size={13} /> {checkoutLabel}
              </button>
            )}
          </div>
        )}

        {/* Right — rate pills */}
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {has(goldRate) && (
            <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-amber-600 whitespace-nowrap">24K Gold</span>
              <div className="w-px h-4 bg-amber-200" />
              <span className="text-sm font-bold text-amber-800 whitespace-nowrap">
                ₹{parseFloat(goldRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
          {has(usdRate) && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-green-600 whitespace-nowrap">USD</span>
              <div className="w-px h-4 bg-green-200" />
              <span className="text-sm font-bold text-green-800 whitespace-nowrap">₹{usdRate}</span>
            </div>
          )}
          {has(gstRate) && (
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-slate-500 whitespace-nowrap">GST</span>
              <div className="w-px h-4 bg-slate-200" />
              <span className="text-sm font-bold text-slate-700">{gstRate}%</span>
            </div>
          )}
          {rightExtra}
        </div>
      </div>
    </div>
  )
}
